import React, { useState } from 'react';
import { TotalExposureData } from '../../services/portfolio';
import { usePrivacy } from '../../contexts/PrivacyContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import LoadingSpinner from '../ui/LoadingSpinner';

interface TotalExposureSectionProps {
  exposureData: TotalExposureData | undefined;
  isLoading: boolean;
}

const pillStyle = (bg: string, color: string): React.CSSProperties => ({
  fontSize: '0.65rem', fontWeight: 600, padding: '2px 7px',
  borderRadius: '999px', backgroundColor: bg, color,
  letterSpacing: '0.03em', display: 'inline-block',
});

const getSecurityTypeBadge = (securityType: string) => {
  const type = securityType.toLowerCase();
  if (type === 'equity') return <span style={pillStyle('#f4f4f5', '#52525b')}>Equity</span>;
  if (type === 'bond')   return <span style={pillStyle('#000537', '#ffffff')}>Bond</span>;
  if (type === 'cash')   return <span style={pillStyle('#f4f4f5', '#52525b')}>Cash</span>;
  return                        <span style={pillStyle('#f4f4f5', '#52525b')}>Other</span>;
};

const gridCols = '1fr 70px 95px 120px 120px 160px 110px 90px';
const DIRECT_COLOR = '#000537';
const ETF_COLOR = '#8c9fd4';

const TotalExposureSection: React.FC<TotalExposureSectionProps> = ({
  exposureData,
  isLoading
}) => {
  const [displayCount, setDisplayCount] = useState(10);
  const { isPrivateMode } = usePrivacy();
  const { formatAmount } = useCurrency();

  if (isLoading) {
    return (
      <div className="card card-forest mb-4">
        <div className="card-body">
          <div className="section-label mb-3">Top Security Exposures</div>
          <div className="d-flex align-items-center justify-content-center py-5">
            <LoadingSpinner text="Loading security exposure data..." />
          </div>
        </div>
      </div>
    );
  }

  if (!exposureData || !exposureData.securities || exposureData.securities.length === 0) {
    return (
      <div className="card card-forest mb-4">
        <div className="card-body">
          <div className="section-label mb-3">Top Security Exposures</div>
          <div className="d-flex align-items-center justify-content-center py-5" style={{ color: '#a1a1aa', fontSize: '0.875rem' }}>
            No securities exposure data available
          </div>
        </div>
      </div>
    );
  }

  const sortedSecurities = [...exposureData.securities]
    .sort((a, b) => b.total_value - a.total_value)
    .slice(0, displayCount);

  return (
    <div className="card h-100" style={{ border: '1px solid #f4f4f5', borderRadius: '12px', overflow: 'hidden' }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #f4f4f5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span className="fw-semibold" style={{ color: '#000537', fontSize: '0.95rem' }}>Top Security Exposures</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '0.75rem', color: '#a1a1aa' }}>Show</span>
          <select
            className="form-select form-select-sm"
            value={displayCount}
            onChange={(e) => setDisplayCount(Number(e.target.value))}
            style={{ width: '72px', fontSize: '0.8rem' }}
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}><div style={{ minWidth: 860 }}>
        {/* Header row */}
        <div style={{
          padding: '10px 20px',
          display: 'grid',
          gridTemplateColumns: gridCols,
          gap: '12px',
          borderBottom: '1px solid #f4f4f5',
        }}>
          <span className="section-label">Security</span>
          <span className="section-label">Type</span>
          <span className="section-label">% Portfolio</span>
          <span className="section-label">Direct</span>
          <span className="section-label">Via ETFs</span>
          <span className="section-label">Composition</span>
          <span className="section-label">Total Value</span>
          <span className="section-label">Total Shares</span>
        </div>

        {sortedSecurities.map((security, idx) => {
          const portfolioPercentage = ((security.total_value / exposureData.total_portfolio_value) * 100).toFixed(2);
          const directPercentage = ((security.direct_value / security.total_value) * 100).toFixed(1);
          const etfPercentage = ((security.etf_value / security.total_value) * 100).toFixed(1);

          return (
            <div
              key={security.identifier}
              style={{
                padding: '11px 20px',
                display: 'grid',
                gridTemplateColumns: gridCols,
                gap: '12px',
                alignItems: 'center',
                borderBottom: idx < sortedSecurities.length - 1 ? '1px solid #f4f4f5' : 'none',
                transition: 'background-color 0.15s ease',
              }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#fafafa')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#18181b', fontFamily: 'monospace' }}>{security.identifier}</div>
                {security.name && (
                  <div style={{ fontSize: '0.75rem', color: '#a1a1aa', marginTop: '1px' }}>{security.name}</div>
                )}
              </div>
              <div>{getSecurityTypeBadge(security.security_type)}</div>
              <div style={{ fontSize: '0.85rem', color: '#52525b', fontVariantNumeric: 'tabular-nums' }}>
                {portfolioPercentage}%
              </div>

              <div>
                <div style={{ fontSize: '0.85rem', color: '#52525b', fontVariantNumeric: 'tabular-nums' }}>
                  {formatAmount(
                    typeof security.direct_value === 'number' ? security.direct_value : Number(security.direct_value),
                    isPrivateMode
                  )}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#a1a1aa', marginTop: '1px' }}>
                  {isPrivateMode ? `${directPercentage}%` : (
                    `${typeof security.direct_shares === 'number' ? security.direct_shares.toFixed(2) : Number(security.direct_shares).toFixed(2)} sh (${directPercentage}%)`
                  )}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.85rem', color: '#52525b', fontVariantNumeric: 'tabular-nums' }}>
                  {formatAmount(
                    typeof security.etf_value === 'number' ? security.etf_value : Number(security.etf_value),
                    isPrivateMode
                  )}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#a1a1aa', marginTop: '1px' }}>
                  {isPrivateMode ? `${etfPercentage}%` : (
                    `${typeof security.etf_shares === 'number' ? security.etf_shares.toFixed(2) : Number(security.etf_shares).toFixed(2)} sh (${etfPercentage}%)`
                  )}
                </div>
              </div>

              {/* Stacked composition bar */}
              <div>
                <div style={{ display: 'flex', height: '6px', borderRadius: '3px', overflow: 'hidden', background: '#f4f4f5' }}>
                  <div style={{ width: `${directPercentage}%`, background: DIRECT_COLOR, transition: 'width 0.4s ease' }} />
                  <div style={{ width: `${etfPercentage}%`, background: ETF_COLOR, transition: 'width 0.4s ease' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                  <span style={{ fontSize: '0.7rem', color: '#71717a', fontVariantNumeric: 'tabular-nums', textAlign: 'left' }}>
                    {directPercentage}% direct
                  </span>
                  <span style={{ fontSize: '0.7rem', color: '#71717a', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                    {etfPercentage}% ETF
                  </span>
                </div>
              </div>
              <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#000537', fontVariantNumeric: 'tabular-nums' }}>
                {formatAmount(
                  typeof security.total_value === 'number' ? security.total_value : Number(security.total_value),
                  isPrivateMode
                )}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#52525b', fontVariantNumeric: 'tabular-nums' }}>
                {isPrivateMode ? '***' : (
                  typeof security.total_shares === 'number'
                    ? security.total_shares.toFixed(2)
                    : Number(security.total_shares).toFixed(2)
                )}
              </div>
            </div>
          );
        })}
      </div></div>
    </div>
  );
};

export default TotalExposureSection;
